import React, { useEffect } from 'react';

export interface NextHeadProps {
  children?: React.ReactNode;
}

export function Head({ children }: NextHeadProps) {
  useEffect(() => {
    if (!children) return;

    const managedElements: Element[] = [];

    React.Children.forEach(children, (child) => {
      if (!React.isValidElement(child)) return;

      const { type, props } = child;

      // 1. Handle <title>
      if (type === 'title') {
        const titleText =
          typeof props.children === 'string'
            ? props.children
            : String(props.children || '');
        if (titleText) {
          document.title = titleText;
        }
        return;
      }

      // 2. Handle <meta> tags
      if (type === 'meta') {
        const { name, property, content } = props as any;
        if (!content) return;

        let selector = '';
        if (name) selector = `meta[name="${name}"]`;
        else if (property) selector = `meta[property="${property}"]`;

        if (selector) {
          let el = document.head.querySelector(selector) as HTMLMetaElement | null;
          if (!el) {
            el = document.createElement('meta');
            if (name) el.setAttribute('name', name);
            if (property) el.setAttribute('property', property);
            document.head.appendChild(el);
            managedElements.push(el);
          }
          el.setAttribute('content', content);
        }
        return;
      }

      // 3. Handle <link> tags (e.g. canonical)
      if (type === 'link') {
        const { rel, href } = props as any;
        if (rel && href) {
          let el = document.head.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
          if (!el) {
            el = document.createElement('link');
            el.setAttribute('rel', rel);
            document.head.appendChild(el);
            managedElements.push(el);
          }
          el.setAttribute('href', href);
        }
        return;
      }

      // 4. Handle JSON-LD structured data <script type="application/ld+json">
      if (type === 'script' && props.type === 'application/ld+json') {
        const jsonContent =
          typeof props.dangerouslySetInnerHTML?.__html === 'string'
            ? props.dangerouslySetInnerHTML.__html
            : typeof props.children === 'string'
              ? props.children
              : '';

        if (jsonContent) {
          const id = props.id || 'schema-jsonld';
          let scriptEl = document.head.querySelector(`script#${id}`) as HTMLScriptElement | null;
          if (!scriptEl) {
            scriptEl = document.createElement('script');
            scriptEl.id = id;
            scriptEl.type = 'application/ld+json';
            document.head.appendChild(scriptEl);
            managedElements.push(scriptEl);
          }
          scriptEl.textContent = jsonContent;
        }
      }
    });
  }, [children]);

  return null;
}

export default Head;
